import { Box2, Matrix23, Matrix34, Vector2, Vector3 } from '@seddi/math-v2';
import { PieceSide } from '@seddi/scene';
import { Box3, FloatType, Sphere, Vector3 as ThreeVector3, } from '@seddi/three';
import { assert } from '@/core/assert';
import { create as createBounds } from '@/core/orientedSphere';
import { getInverseTransformMatrix, getTransformMatrix } from '@/core/transformRt2';
function getBoxLocal(self, ref) {
    const { size } = self;
    ref.max.set(size.x * 0.5, size.y * 0.5);
    ref.min.set(size.x * -0.5, size.y * -0.5);
}
export const computeBoundsFromGeometry = (() => {
    const boxLocal = new Box2();
    const boxWorld = new Box3();
    const worldToLocal = new Matrix23();
    const v2 = new Vector2();
    const v3 = new ThreeVector3();
    return (region, geometry) => {
        getBoxLocal(region, boxLocal);
        getInverseTransformMatrix(region.transform, worldToLocal);
        boxWorld.makeEmpty();
        const positionAttr = geometry.getAttribute('position');
        const normalAttr = geometry.getAttribute('normal');
        const uvAttr = geometry.getAttribute('uv');
        assert(positionAttr === null || positionAttr === void 0 ? void 0 : positionAttr.isBufferAttribute, 'Attribute missing');
        assert(positionAttr.gpuType === FloatType, 'Attribute has unsupported GPU type');
        assert(normalAttr === null || normalAttr === void 0 ? void 0 : normalAttr.isBufferAttribute, 'Attribute missing');
        assert(normalAttr.gpuType === FloatType, 'Attribute has unsupported GPU type');
        assert(uvAttr === null || uvAttr === void 0 ? void 0 : uvAttr.isBufferAttribute, 'Attribute missing');
        assert(uvAttr.gpuType === FloatType, 'Attribute has unsupported GPU type');
        if (!positionAttr || !normalAttr || !uvAttr) {
            return createBounds();
        }
        const normal = new ThreeVector3();
        const sphere = new Sphere();
        // NOTE: We may need to visit faces and clip them if geometry is not well tessellated
        for (let i = 0, n = positionAttr.count; i < n; i++) {
            v2.set(uvAttr.getX(i), uvAttr.getY(i));
            worldToLocal.transformRef(v2);
            if (boxLocal.intersectsPoint(v2)) {
                v3.set(positionAttr.getX(i), positionAttr.getY(i), positionAttr.getZ(i));
                // Apply matrixWorld? boxWorld vs boxObject
                boxWorld.expandByPoint(v3);
                v3.set(normalAttr.getX(i), normalAttr.getY(i), normalAttr.getZ(i));
                normal.add(v3);
            }
        }
        boxWorld.getBoundingSphere(sphere);
        // Fallback: no vertices inside region - sample four corners for closest vertices
        const usedFallback = normal.lengthSq() === 0;
        if (usedFallback) {
            const cornerResult = sampleRegionCorners(region, i => ({ x: uvAttr.getX(i), y: uvAttr.getY(i) }), (i, pos, n) => {
                pos.set(positionAttr.getX(i), positionAttr.getY(i), positionAttr.getZ(i));
                n.set(normalAttr.getX(i), normalAttr.getY(i), normalAttr.getZ(i));
            }, positionAttr.count);
            if (cornerResult) {
                cornerResult.box.getBoundingSphere(sphere);
                normal.copy(cornerResult.normal);
            }
            else {
                // No vertices near corners - use geometry bounding sphere
                if (geometry.boundingSphere === null) {
                    geometry.computeBoundingSphere();
                }
                normal.set(0, 0, 1);
                if (geometry.boundingSphere) {
                    sphere.copy(geometry.boundingSphere);
                }
            }
        }
        else {
            normal.normalize();
        }
        const { center } = sphere;
        const bounds = {
            center: { x: center.x, y: center.y, z: center.z },
            normal: { x: normal.x, y: normal.y, z: normal.z },
            radius: sphere.radius,
        };
        return bounds;
    };
})();
/**
 * Sample the four corners of a region in UV space and find the closest mesh vertices.
 * Returns positions and normals from those vertices for accurate frame bounds when
 * no vertices fall inside the region for small regions.
 */
const sampleRegionCorners = (() => {
    const m = new Matrix23();
    const v2 = new Vector2();
    const v3 = new ThreeVector3();
    const v3b = new ThreeVector3();
    return (region, getVertexUV, getVertexPosNormal, vertexCount) => {
        const { size } = region;
        const halfX = size.x * 0.5;
        const halfY = size.y * 0.5;
        getTransformMatrix(region.transform, m);
        const corners = [
            [-halfX, -halfY],
            [halfX, -halfY],
            [halfX, halfY],
            [-halfX, halfY],
        ];
        const box = new Box3();
        const normal = new ThreeVector3();
        let foundAny = false;
        for (const [lx, ly] of corners) {
            v2.set(lx, ly);
            m.transformRef(v2);
            let bestIdx = -1;
            let bestDistSq = Infinity;
            for (let i = 0; i < vertexCount; i++) {
                const uv = getVertexUV(i);
                const dx = uv.x - v2.x;
                const dy = uv.y - v2.y;
                const distSq = dx * dx + dy * dy;
                if (distSq < bestDistSq) {
                    bestDistSq = distSq;
                    bestIdx = i;
                }
            }
            if (bestIdx >= 0) {
                getVertexPosNormal(bestIdx, v3, v3b);
                box.expandByPoint(v3);
                normal.add(v3b);
                foundAny = true;
            }
        }
        if (!foundAny)
            return null;
        if (normal.lengthSq() > 0) {
            normal.normalize();
        }
        return { box, normal };
    };
})();
export const finishFrameBounds = (() => {
    const RADIUS_MARGIN = 0.075;
    const RADIUS_MIN = 0.1;
    const BACK_NECK_LABEL_R = new Matrix34();
    BACK_NECK_LABEL_R.makeRotationX(-Math.PI * 0.2);
    const Y = new Vector3(0, 1, 0);
    const m1 = new Matrix34();
    const xAxis = new Vector3();
    const yAxis = new Vector3();
    return (self, target) => {
        // Adjust radius with limits.
        target.radius = Math.max(RADIUS_MIN, target.radius + RADIUS_MARGIN);
        // Invert normal orientation when the region is on the BACK side
        if (self.side === PieceSide.BACK) {
            target.normal.x = -target.normal.x;
            target.normal.y = -target.normal.y;
            target.normal.z = -target.normal.z;
        }
        // Add an extra rotation to Back Neck Label regions to see through neck holes
        if (self.name.localeCompare('Back Neck Label', undefined, { sensitivity: 'accent' }) === 0) {
            const zAxis = target.normal;
            Vector3.CrossToRef(Y, zAxis, xAxis);
            Vector3.CrossToRef(zAxis, xAxis, yAxis);
            m1.setRotationFromBasis(xAxis, yAxis, zAxis);
            target.normal.x = 0;
            target.normal.y = 0;
            target.normal.z = 1;
            m1.multiplyInPlace(BACK_NECK_LABEL_R).transformRef(target.normal);
        }
    };
})();
